/**
 * SalesDashboard.tsx — Sales / Dashboard, mockup-parity layout (§10, P10-1..3).
 *
 * Mockup structure (klaxiora-crm-mockup.html:356-441): greeting head + New
 * Deal button → four icon-less KPI cards → [Revenue | Deals by stage] →
 * [Top open deals | Recent activity]. Cards were VALUE-ONLY (P10-2/R6-1: no
 * fabricated deltas) until 2.1b: the "Open pipeline value" and "Open deals"
 * cards now carry the k-metric-delta line from the /v1/sales/reports trend
 * wire (§16.2.4, ruled §22.5; copy P2-15 — "—" plus the server's reason
 * while no honest prior exists, never a fake 0, never hidden; trendDeltas.ts).
 * Win rate and Closed this month have no member on that wire (the fact
 * deltas are a later slice) and stay value-only. The pre-parity extras
 * (weighted pipeline, avg deal size,
 * all-time closed won, the right-rail summary) render on Overview home and
 * Reports — removed from THIS page only (P10-1).
 *
 * Live data: KPIs + revenue from /v1/sales/reports via the summary, funnel
 * from the server's zero-filled 5-row spine (NEVER /stats dealsByStage,
 * which drops empty stages), deals + activity from their real lists.
 *
 * SB-12: the four KPI cards and the funnel rows drill in-page to the deal
 * rows behind each figure, through the SHARED ReportsDrillView +
 * drillShortfall (SalesReports.tsx / reportsDrill.ts) — one drill component,
 * one reconciliation path, the R7-1..6 state-swap shape. Win rate is the
 * server's closed-cohort winRateClosedPct (definition A), NOT the legacy
 * client-derived dealsWinRate.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical design system).
 */
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { DealStageBadge, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchSalesSummary } from '../../api'
import { currencySymbol } from '../../../../lib/money'
import { NewDealDrawer } from './SalesPipeline'
import { topOpenDeals, closedThisMonthPence, dealsByCompany, companyDeals } from './dashboardView'
import { deltaLine, historySinceLine, type DeltaLine, type DeltaTone } from './trendDeltas'
import { ReportsDrillView } from './ReportsDrillView'
import { type ReportsDrill } from './reportsDrill'
import { RevenueByMonthChart } from './RevenueByMonthChart'
import { excludedSuffix } from './fenceCopy'
import { DealsByStageRows } from './DealsByStageRows'

// Takes integer PENCE (the summary/deal money unit since the D9 switch).
// Per-row calls pass the deal's ORIGINAL currency (0111: non-GBP deals
// exist once captured); aggregate figures stay £ — they sum the
// GBP-comparable leg.
export function fmtMoney(pence: number, currency = 'GBP') {
  // SB-10: same swap as fmtCompact — shared symbol rule, spacing and
  // compaction byte-identical.
  const s = currencySymbol(currency)
  const sym = s === currency ? `${s} ` : s
  const n = pence / 100
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}k`
  return `${sym}${n.toFixed(0)}`
}

// The k-metric-delta contract: "color = status token" (klaxiora.css). Up and
// down read through the existing trend classes (success / danger tokens),
// flat through the neutral one, and an absent line is quiet ink — the words
// carry the meaning there, not a colour.
const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  up: 'trend-up', down: 'trend-down', flat: 'trend-neutral', absent: 'text-ink-muted',
}

/**
 * The k-metric widget for the Sales pages (S-Tier slice 3) — the OpsMetric
 * precedent verbatim: it lives WITH the Sales pages and is markup-level reuse
 * of the k-* contract, so DashboardShared's MetricCard and its non-Sales
 * consumers stay untouched. Value with an optional note line and, since 2.1b,
 * the optional `.k-metric-delta` line under the value (§10.C/§11.C reserved
 * the slot): present → "▲ £12k vs 1 Aug"; absent → "—" plus the server's
 * reason; a card whose metric has no member on the wire passes nothing and
 * renders exactly as before.
 */
export function SalesMetric({ label, value, note, delta, onClick }: {
  label: string; value: string | number; note?: string
  /** 2.1b: the trend line from trendDeltas.deltaLine — null/undefined
   *  renders no line (the wire has no member for this card). */
  delta?: DeltaLine | null
  /** SB-12: a card with a drill behind it renders as a real <button>
   *  (keyboard-reachable). Resting render is class-identical to the div —
   *  .k-metric overrides every button UA style, so the pixel pin holds
   *  (the R7-5 doctrine: affordances are hover/focus-only). */
  onClick?: () => void
}) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value">{value}</span>
        </div>
        {delta && <div className={`k-metric-delta ${DELTA_TONE_CLASS[delta.tone]}`}>{delta.text}</div>}
        {note && <div className="k-metric-note">{note}</div>}
      </div>
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
        className="k-metric w-full text-left cursor-pointer focus:outline-none">
        {inner}
      </button>
    )
  }
  return <div className="k-metric">{inner}</div>
}

export function SalesDashboard({ onNavigate, onSelectDeal, sessionUser }: {
  onNavigate: (page: string) => void
  onSelectDeal?: (id: string) => void
  /** The session user's display name (else email) from the shell's /v1/me
   *  state; null until loaded — the greeting renders nameless. */
  sessionUser?: string | null
}) {
  // Bumped after a drawer create so useAsync re-runs and the new deal shows.
  const [refreshKey, setRefreshKey] = useState(0)
  const [showNewDeal, setShowNewDeal] = useState(false)
  // SB-12: in-page drill state (the R7-1..6 shape — no URL router, no
  // cross-page filter navigation). Rendered through the SHARED
  // ReportsDrillView; this page adds no drill machinery of its own.
  const [drill, setDrill] = useState<ReportsDrill | null>(null)
  const { data, loading, error } = useAsync(fetchSalesSummary, [refreshKey])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  if (drill) {
    return <ReportsDrillView drill={drill} onBack={() => setDrill(null)}
      onSelectDeal={onSelectDeal} backLabel="Back to dashboard"
      // SB-13: the company drill filters the SAME list its rollup row was
      // computed from — never a refetch, so drill and number cannot diverge.
      sourceDeals={drill.kind === 'company' ? companyDeals(data!.deals, drill.name) : undefined} />
  }

  const { summary, deals, activities } = data!
  const openDeals = topOpenDeals(deals)
  const companyRollup = dealsByCompany(deals)
  const recentActivities = activities.slice(0, 5)
  // Mockup greets by first name ("Good morning, Dylan"); an email session
  // user greets by the full address rather than a fabricated split.
  const firstName = sessionUser && !sessionUser.includes('@')
    ? sessionUser.trim().split(/\s+/)[0]
    : sessionUser

  // The mockup's four cards, each clickable to the rows behind its figure
  // (SB-12). Win rate is the SERVER closed-cohort rate (won ÷ won+lost,
  // whole population) — absent (pre-SB-12 backend, or no closed deals yet)
  // renders an em dash, never a fake 0%. Open-pipeline and Open-deals share
  // ONE composite open drill: two figures, same population.
  // 2.1b: those two cards also carry the delta line — the wire's
  // pipelinePence / openDealCount members against the live figures from
  // the SAME payload (§22.6: the screen takes the difference). Win rate and
  // Closed this month have no member on the wire, so no line (nothing to
  // hide, nothing fabricated).
  const openDrill: ReportsDrill = { kind: 'open', openDeals: summary.openDeals, pipelinePence: summary.totalPipeline }
  const lastBucket = summary.monthlyRevenue.length > 0
    ? summary.monthlyRevenue[summary.monthlyRevenue.length - 1] : null
  const d = summary.deltas
  const history = historySinceLine(summary.historySince)
  // §26 F-1: openDeals is the FENCED count; the rows the fence dropped ride
  // the card's note as "(+k excluded)" (F-1d) — nothing at 0.
  const excludedNote = excludedSuffix(summary.openExcludedNonGbp) || undefined
  const KPIS: { label: string; value: string; note?: string; drill?: ReportsDrill; delta?: DeltaLine | null }[] = [
    { label: 'Open pipeline value', value: fmtMoney(summary.totalPipeline), note: excludedNote, drill: openDrill,
      delta: deltaLine(d?.pipelinePence, summary.totalPipeline, d?.absent?.pipelinePence, fmtMoney) },
    { label: 'Open deals',          value: String(summary.openDeals), note: excludedNote, drill: openDrill,
      delta: deltaLine(d?.openDealCount, summary.openDeals, d?.absent?.openDealCount, n => `${n} ${n === 1 ? 'deal' : 'deals'}`) },
    { label: 'Win rate',
      value: summary.winRateClosedPct != null ? `${Math.round(summary.winRateClosedPct)}%` : '—',
      drill: summary.wonCount != null && summary.lostCount != null
        ? { kind: 'closed', wonCount: summary.wonCount, lostCount: summary.lostCount } : undefined },
    { label: 'Closed this month',   value: fmtMoney(closedThisMonthPence(summary.monthlyRevenue)),
      drill: lastBucket ? { kind: 'month', month: lastBucket.month, wonPence: lastBucket.won } : undefined },
  ]

  return (
    <div className="page-enter flex flex-col gap-4">
      {/* Greeting head (mockup :357-366). "Good morning" is static, mockup-
          verbatim — a clock-derived greeting would make the pixel pin vary
          by time of day. */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-section-title font-bold text-ink-primary">
            Good morning{firstName ? `, ${firstName}` : ''}
          </h2>
          <p className="text-13 text-ink-muted mt-1">Here's how the sales pipeline is tracking this month.</p>
        </div>
        <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowNewDeal(true)}>
          <Plus size={14} /> New Deal
        </button>
      </div>

      {/* Four KPI cards (mockup :368-388) — .k-metric; since 2.1b the two
          pipeline cards carry the delta line (absent → "—" + the server's
          reason, P2-15), the other two stay value-only (no member on the
          wire). Each drills to the rows behind its number (SB-12); a card
          whose drill context is absent stays a plain div. */}
      <div className="grid grid-cols-4 gap-3">
        {KPIS.map(k => (
          <SalesMetric key={k.label} label={k.label} value={k.value} note={k.note} delta={k.delta}
            onClick={k.drill ? () => setDrill(k.drill!) : undefined} />
        ))}
      </div>
      {/* P2-15: the history floor, from the ledger — never hidden while the
          wire carries it; absent only on a pre-2.1b backend. */}
      {history && <p className="text-12 text-ink-muted">{history}</p>}

      {/* Row 1: Revenue | Deals by stage (mockup :390-414) */}
      <div className="grid grid-cols-[1.4fr_1fr] gap-4">
        {/* Revenue by month (closed-won value, /v1/sales/reports) — honest
            labels kept over the mockup's "Revenue trend" (P10-5, §6.B pin).
            §24 C-8: the SHARED chart (RevenueByMonthChart — the k-chart
            contract, C-2..C-5 + C-9 inside it); this page passes no drill,
            so its bars are inert as they always were. */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Revenue by month</div>
              <div className="k-card-sub">Closed-won value, last 6 months</div>
            </div>
          </div>
          <div className="p-4">
            <RevenueByMonthChart data={summary.monthlyRevenue.map(m => ({ month: m.month, wonPence: m.won }))} />
          </div>
        </div>

        {/* Deals by stage — the server's zero-filled 5-row spine
            (summary.funnel), so every stage renders even at 0. The SHARED
            rows (§24 C-6 / C-7: DealsByStageRows — open tracks scaled to the
            OPEN max, Closed Won kept as the fifth row but off the track);
            each row drills to the stage's deal list (SB-12). */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Deals by stage</div>
          </div>
          <DealsByStageRows funnel={summary.funnel}
            onDrill={r => setDrill({ kind: 'stage', stage: r.stage, label: r.label, count: r.count })} />
        </div>
      </div>

      {/* Row 2: Top open deals | Recent activity (mockup :416-440) */}
      <div className="grid grid-cols-[1.4fr_1fr] gap-4">
        {/* Top open deals — open stages by value, capped rows; the input list
            is the limit-200 newest-first call (see dashboardView.ts). */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Top open deals</div>
          </div>
          {openDeals.length === 0 ? (
            <EmptyState message="No open deals" />
          ) : (
            <table className="closr-table sales-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Deal</th>
                  <th className="text-left">Company</th>
                  <th className="text-left">Stage</th>
                  <th className="text-left">Value</th>
                </tr>
              </thead>
              <tbody>
                {openDeals.map(d => (
                  <tr key={d.id} className="cursor-pointer hover:bg-surface-row-hover transition-colors"
                    onClick={() => onSelectDeal?.(d.id)}>
                    <td className="font-medium text-ink-primary">{d.name}</td>
                    <td className="text-13 text-ink-body">{d.company}</td>
                    <td><DealStageBadge stage={d.stage} /></td>
                    <td className="font-semibold text-ink-primary">{fmtMoney(d.amount, d.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent activity — real /v1/sales/activities; an empty tenant shows
            the true empty state (never faked). */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Activity</div>
            <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
              onClick={() => onNavigate('sales.activities')}>See All</button>
          </div>
          <div className="divide-y divide-edge-row-faint">
            {recentActivities.length === 0 ? (
              <div className="px-4 py-6 text-center text-12 text-ink-muted">No recent activity</div>
            ) : (
              recentActivities.map(a => (
                <div key={a.id} className="px-4 py-3 hover:bg-surface-row-hover transition-colors">
                  <div className="flex items-start gap-2">
                    <div className={`mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full ${a.type === 'email' ? 'bg-accent' : a.type === 'call' ? 'bg-success' : a.type === 'meeting' ? 'bg-info' : 'bg-warning'}`} />
                    <div className="flex-1 min-w-0">
                      {/* "<subject> — <body>": the feed line's lead is the
                          activity's title. (It always was — the old mapper
                          had put the subject in a `contact` slot.) */}
                      <p className="text-12 text-ink-body leading-snug">
                        <span className="font-semibold text-ink-primary">{a.subject}</span>
                        {a.description ? ` — ${a.description}` : ''}
                      </p>
                      <div className="text-overline text-ink-faint mt-1">{a.time}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Deals by company (SB-2 item 5) — client-side rollup over the
          page's own deals fetch (recon decision: no endpoint at this scale).
          String-grouped: company is free text on the deal, so case/spelling
          variants split rows — the Top-performers v1 limit, worn openly. */}
      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Deals by company</div>
            <div className="k-card-sub">All deals, grouped by the company on the deal</div>
          </div>
        </div>
        {companyRollup.length === 0 ? (
          <EmptyState message="No deals yet" />
        ) : (
          <table className="closr-table sales-table w-full">
            <thead>
              <tr>
                <th className="text-left">Company</th>
                <th className="text-right">Deals</th>
                <th className="text-right">Won</th>
                <th className="text-right">Open</th>
                <th className="text-right">Open pipeline</th>
                <th className="text-right">Won value</th>
              </tr>
            </thead>
            <tbody>
              {companyRollup.slice(0, 8).map(c => {
                // SB-13: rows drill to the company's deals — the Top-
                // performers tr pattern (a <button> cannot legally wrap a
                // table row); keyboard-reachable, resting classes carry
                // only hover/cursor affordances so the pixel pin holds.
                const go = () => setDrill({
                  kind: 'company', name: c.company, total: c.total, won: c.won,
                  open: c.open, pipelinePence: c.pipelinePence, wonPence: c.wonPence,
                })
                return (
                  <tr key={c.company} tabIndex={0} onClick={go}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
                    className="cursor-pointer hover:bg-surface-row-hover transition-colors focus:outline-none">
                    <td className="font-medium text-ink-primary">{c.company}</td>
                    <td className="text-right text-13 text-ink-body">{c.total}</td>
                    <td className="text-right text-13 text-ink-body">{c.won}</td>
                    <td className="text-right text-13 text-ink-body">{c.open}</td>
                    <td className="text-right font-semibold text-ink-primary">{fmtMoney(c.pipelinePence)}</td>
                    <td className="text-right text-13 text-ink-body">{fmtMoney(c.wonPence)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {companyRollup.length > 8 && (
          <div className="px-4 py-2 text-12 text-ink-muted">and {companyRollup.length - 8} more companies</div>
        )}
      </div>

      {showNewDeal && (
        <NewDealDrawer
          initialStage="New Lead"
          onClose={() => setShowNewDeal(false)}
          onCreated={() => setRefreshKey(k => k + 1)}
        />
      )}
    </div>
  )
}
